import { Checkbox, Flex, Slider, Table, ConfigProvider } from "antd";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import TagBordered from "../../../../../../../../../../atom/TagBordered";

import StyledContainer from "./style";

import { MODEL_INVESTMENT_AMOUNT } from "../../../../constant";
import { TAG_COLOR_LIST } from "../../../../../../../../../../atom/TagBordered/constant";

import {
  getCurrencyValue,
  getCurrencyValueWithRoundSign,
  getPercentValue,
} from "../../../../../../../../../../utils/helper/general";
import { getRiskToleranceByRiskScore } from "../../../../../../../advisor/helper";

import { ReactComponent as SliderPlaceholder } from "../../image/slider_placeholder.svg";
import { ReactComponent as Warning } from "../../image/icon_warning.svg";

const Aside = ({
  calculation,
  hideExtraValues,
  hideSectionBeAware,
  investmentAmount = MODEL_INVESTMENT_AMOUNT,
  modelData,
}) => {
  const columns = [
    {
      render: allValues => <TagBordered label={allValues.category} />,
      title: "Category",
    },
    {
      render: allValues => getCurrencyValueWithRoundSign(allValues.amount),
      title: "amt",
    },
    {
      render: allValues => getPercentValue(allValues.percentage),
      title: "%",
    },
  ];

  const options = {
    chart: {
      type: "pie",
      height: 110,
      width: 110,
      backgroundColor: "transparent",
    },
    xAxis: {
      visible: false,
    },
    tooltip: {
      enabled: false,
      outside: true,
      formatter: function () {
        return `${this.key}: <b>${this.y}</b> time(s)`;
      },
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        size: 83,
        innerSize: "60%",
        dataLabels: {
          enabled: false,
        },
        center: ["50%", "50%"],
        borderColor: "#ffffff",
        borderWidth: 2,
        states: {
          hover: {
            enabled: true,
            brightness: 0,
          },
        },
      },
    },
    exporting: {
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
    colors: ["#e8e8e8"],
    series: [
      {
        type: "pie",
        data: calculation?.tableCategory?.map(it => ({
          color: TAG_COLOR_LIST.find(tag => tag.label === it.category)?.color,
          name: it.category,
          y: it.percentage,
        })),
      },
    ],
  };

  const getSliderValue = value => {
    if (!value) return 4;

    if (value > 96) return 96;

    return value;
  };

  const ContentEmptyModel = () => (
    <StyledContainer>
      <h3>Model Summary</h3>

      <Flex justify="space-between" style={{ color: "#4B4747" }}>
        <Flex vertical>
          <span>Closest to</span>
          <h4>n/a</h4>
        </Flex>
        <Flex style={{ textAlign: "right" }} vertical>
          <span>Risk Score</span>
          <h4>n/a</h4>
        </Flex>
      </Flex>

      <SliderPlaceholder />

      <Flex align="center" vertical>
        <HighchartsReact highcharts={Highcharts} options={options} />

        <Flex style={{ color: "#737373", width: "100%" }} vertical>
          <Flex gap={12} justify="end" style={{ padding: 8 }}>
            <span style={{ width: 82 }}>Category</span>
            <span style={{ textAlign: "right", width: 82 }}>Holdings</span>
            <span style={{ textAlign: "right", width: 82 }}>%</span>
          </Flex>
          <div className="container-value" style={{ height: 66 }} />
        </Flex>
      </Flex>

      <div className="container-value">
        <p>Investment Amount</p>
        <h5>{getCurrencyValue(investmentAmount)}</h5>
      </div>

      {!hideExtraValues && (
        <>
          <Flex gap={19}>
            <div className="container-value">
              <p>Expected Return</p>
              <h5>n/a</h5>
            </div>
            <div className="container-value">
              <p>Std. Deviation</p>
              <h5>n/a</h5>
            </div>
          </Flex>

          <Flex gap={19}>
            <div className="container-value">
              <p>Max Drawdown</p>
              <h5>n/a</h5>
            </div>
            <div className="container-value">
              <p style={{ marginTop: 6 }}>Sortino</p>
              <h5>n/a</h5>
            </div>
            <div className="container-value">
              <p>Sharpe Ratio</p>
              <h5>n/a</h5>
            </div>
          </Flex>
        </>
      )}
    </StyledContainer>
  );

  const ContentSavedModel = () => (
    <StyledContainer>
      <h3>Model Summary</h3>

      <Flex justify="space-between" style={{ color: "#4B4747" }}>
        <Flex vertical>
          <span>Closest to</span>
          <h4>{calculation?.statistics?.riskType}</h4>
        </Flex>
        <Flex style={{ textAlign: "right" }} vertical>
          <span>Risk Score</span>
          <h4>{calculation?.statistics?.riskScore}</h4>
        </Flex>
      </Flex>

      <div style={{ overflow: "hidden" }}>
        <Slider
          disabled
          value={getSliderValue(calculation?.statistics?.riskScore)}
          min={0}
          max={100}
          step={1}
          tooltip={{ open: false }}
        />
      </div>

      <Flex gap={11}>
        <HighchartsReact highcharts={Highcharts} options={options} />

        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: "#fff",
                headerColor: "#737373",
                headerSplitColor: "transparent",
              },
            },
          }}
        >
          <Table
            bordered={false}
            columns={columns}
            dataSource={calculation?.tableCategory}
            pagination={false}
            rowHoverable={false}
            size="small"
          />
        </ConfigProvider>
      </Flex>

      {!hideSectionBeAware &&
        getRiskToleranceByRiskScore(
          calculation?.statistics?.riskScore
        )?.toLowerCase() !== modelData?.RiskType?.toLowerCase() && (
          <Flex
            gap={8}
            style={{
              background: "rgba(234, 244, 244, 0.5)",
              border: "1px solid #CFCBC3",
              borderRadius: 8,
              padding: 16,
            }}
          >
            <Warning />
            <Flex flex="1 1 231px" vertical>
              <b>Be aware...</b>
              <span>
                Your personal risk score <b>does not match</b> the portfolio’s
                risk score.
              </span>
              <Flex
                align="center"
                gap={8}
                style={{
                  background: "#FFF",
                  borderRadius: 8,
                  marginTop: 8,
                  padding: "4px 8px",
                }}
              >
                <Checkbox> I Understand</Checkbox>
              </Flex>
            </Flex>
          </Flex>
        )}

      <div className="container-value">
        <p>Investment Amount</p>
        <h5>{getCurrencyValue(investmentAmount)}</h5>
      </div>

      {!hideExtraValues && (
        <>
          <Flex gap={19}>
            <div className="container-value">
              <p>Expected Return</p>
              <h5>
                {getPercentValue(
                  Math.round(calculation?.statistics?.expectedReturn * 100) /
                    100
                )}
              </h5>
            </div>
            <div className="container-value">
              <p>Std. Deviation</p>
              <h5>
                {getPercentValue(
                  Math.round(calculation?.statistics?.standardDeviation * 100) /
                    100
                )}
              </h5>
            </div>
          </Flex>

          <Flex gap={16}>
            <div className="container-value" style={{ width: 94 }}>
              <p>Max Drawdown</p>
              <h5>
                {getPercentValue(
                  Math.round(calculation?.statistics?.maxDrawdown * 100) / 100
                )}
              </h5>
            </div>
            <div className="container-value" style={{ width: 94 }}>
              <p style={{ marginTop: 6 }}>Sortino</p>
              <h5>
                {getPercentValue(
                  Math.round(calculation?.statistics?.sortino * 100) / 100
                )}
              </h5>
            </div>
            <div className="container-value" style={{ width: 94 }}>
              <p>Sharpe Ratio</p>
              <h5>
                {getPercentValue(
                  Math.round(calculation?.statistics?.sharpe * 100) / 100
                )}
              </h5>
            </div>
          </Flex>
        </>
      )}
    </StyledContainer>
  );

  return calculation ? <ContentSavedModel /> : <ContentEmptyModel />;
};

export default Aside;
